import posthog from "posthog-js";
import React, { ChangeEvent, FC, MouseEvent, useEffect, useState } from "react";
import { useIdToken } from "react-firebase-hooks/auth";
import { useSearchParams } from "react-router-dom";

import { Backdrop, CircularProgress, Container, Stack } from "@mui/material";

import {
  captureProductEvent,
  productAnalyticsEvents,
} from "../../analytics/events";
import Header from "../../components/Header";
import Page from "../../components/Page";
import SaveButton from "../../components/SaveButton";
import TextField from "../../components/styled/TextField";
import { firebaseAuth } from "../../configs/firebase";
import { useSelector } from "../../store";
import { useCreateCheckoutSessionMutation } from "../../store/pomeriumApi";
import { buildCancelUrl, buildSuccessUrl } from "./billing-urls";

const Upgrade: FC = () => {
  const { organizationId } = useSelector((state) => state.current);
  const [createCheckoutSession, { isLoading }] =
    useCreateCheckoutSessionMutation();
  const [searchParams] = useSearchParams();
  const [user] = useIdToken(firebaseAuth);
  const [billingEmail, setBillingEmail] = useState(user?.email || "");
  const [organizationName, setOrganizationName] = useState("");

  useEffect(() => {
    captureProductEvent(productAnalyticsEvents.billingUpgradeViewed, {
      return_url: searchParams.get("returnUrl") || undefined,
    });
  }, [searchParams]);

  const onChangeBillingEmail = (evt: ChangeEvent<HTMLInputElement>) => {
    setBillingEmail(evt.target.value);
    posthog.capture("billing_email_updated");
  };

  const onChangeOrganizationName = (evt: ChangeEvent<HTMLInputElement>) => {
    setOrganizationName(evt.target.value);
    posthog.capture("organization_name_changed");
  };

  const onUpgrade = (evt: MouseEvent) => {
    evt.preventDefault();

    (async () => {
      const result = await createCheckoutSession({
        organizationId,
        createCheckoutSessionRequest: {
          billingEmail,
          organizationName,
          cancelUrl: buildCancelUrl(searchParams.get("returnUrl")),
          successUrl: buildSuccessUrl(searchParams.get("returnUrl")),
        },
      });
      if ("data" in result && result.data?.url) {
        location.href = result.data.url;
      }
    })();
  };

  return (
    <>
      <Backdrop open={isLoading}>
        <CircularProgress />
      </Backdrop>
      <Page title="Upgrade">
        <Container>
          <Header title="Upgrade" />
          <Stack gap={3}>
            <TextField
              label="Organization Name"
              value={organizationName}
              onChange={onChangeOrganizationName}
            />
            <TextField
              label="Billing Email"
              value={billingEmail}
              onChange={onChangeBillingEmail}
            />
            <SaveButton
              variant="contained"
              disabled={!billingEmail || !organizationName}
              onClick={onUpgrade}
            >
              Upgrade
            </SaveButton>
          </Stack>
        </Container>
      </Page>
    </>
  );
};

export default Upgrade;
